import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import LanguageSelector from '../components/LanguageSelector';

export default function LoginPage() {
  const { login, user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password, { remember });
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || t('login.errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center py-5">
      {/* Language Selector - Positioned at top right */}
      <div className="position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4">
            <div className="text-center mb-4">
              <Link to="/" className="text-decoration-none">
                <span className="fs-3 fw-semibold text-primary">{t('app.name')}</span>
              </Link>
              <p className="text-muted small mt-2 mb-0">{t('login.accessPrompt')}</p>
            </div>
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <h1 className="h5 mb-3">{t('login.title')}</h1>
                <p className="text-muted small mb-4">{t('login.subtitle')}</p>
                {error && (
                  <div className="alert alert-danger py-2 small" role="alert">{error}</div>
                )}
                <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                  <div className="mb-3">
                    <label htmlFor="loginEmail" className="form-label">{t('login.email')}</label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="bi bi-envelope" aria-hidden="true"></i></span>
                      <input
                        id="loginEmail"
                        type="email"
                        className="form-control"
                        placeholder={t('login.emailPlaceholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label htmlFor="loginPassword" className="form-label d-flex justify-content-between align-items-center">
                      <span>{t('login.password')}</span>
                      <button
                        type="button"
                        className="btn btn-link p-0 small text-decoration-none"
                        onClick={() => setShowPassword(v => !v)}
                      >
                        {showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      </button>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text"><i className="bi bi-lock" aria-hidden="true"></i></span>
                      <input
                        id="loginPassword"
                        type={showPassword ? 'text' : 'password'}
                        className="form-control"
                        placeholder={t('login.passwordPlaceholder')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="rememberCheck"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="rememberCheck">{t('login.rememberMe')}</label>
                    </div>
                  </div>
                  <div className="d-grid mb-2">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                      {loading ? t('login.loggingIn') : t('login.loginButton')}
                    </button>
                  </div>
                </form>
                {/* Registro deshabilitado: la creación de cuentas se gestiona desde otro módulo */}
              </div>
            </div>
            <p className="text-center text-muted mt-4 small mb-0">© {new Date().getFullYear()} {t('app.name')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}